/**
 * Product Linker Render
 *
 * Renders product selection previews and empty states for post linking.
 */

import { Roles } from "../../constants/roles";
import { phpUrl } from "../../core/php-data-access";
import { translator } from "../../translation/translator";
import {
    resolveCategorySymbol,
    buildCategoryPlaceholderHtml,
    buildCategoryIconHtml,
} from "../../components/category-icon/resolve-category-symbol";
import { buildCategoryBreadcrumbHtml } from "../../components/category-icon/category-breadcrumb";
import { buildBrandIconHtml } from "../../components/menu-icon/brand-icon";
import { isDefaultNoImagePath } from "../../util/product-image";
import { renderProductRatingStatsHtml } from "../../components/product-rating/product-rating-summary-render";
import { CommonUtils } from "../../util/common-utils";
import type { UserModalFavorite, UserModalPost } from "../../user/modal/user-modal-api";
import { ProductLinkerForm } from "./product-linker-form";
import { ProductLinkerListener } from "./product-linker-listener";
import type { ProductLinkerProduct } from "./product-linker-api";

const productLinkerForm = new ProductLinkerForm(document.documentElement);

type ProductLinkerRenderableSource = ProductLinkerProduct | UserModalPost | UserModalFavorite;

export type ProductLinkerRenderableProduct = ProductLinkerRenderableSource & {
    image_url?: string | null;
    image_path?: string | null;
    general_description?: string | null;
    recommend_rank?: number | null;
    recommendRank?: number | null;
    brand?: {
        brand_id?: number;
        brand_name?: string;
    } | null;
    category?: {
        category_id?: number;
        category_name?: string;
        parent_category_id?: number | null;
        parent_category_name?: string | null;
    } | null;
};

class ProductLinkerRender {
    public static renderEmpty(previewEl: HTMLElement | null): void {
        if (!previewEl) return;

        previewEl.innerHTML = `
            <div class="post-product-linker__empty" data-translate="post_target_product_empty">ยังไม่ได้ตั้งค่า</div>
        `;

        translator.updateUITranslations();
    }

    public static renderProduct(
        previewEl: HTMLElement | null,
        product: ProductLinkerRenderableProduct,
        options: { targetBlank?: boolean; metaDirection?: string } = {},
    ): void {
        if (!previewEl) return;

        const isPostCardContext = !!productLinkerForm.getClosestCardBody(previewEl);
        const resolvedMetaDirection = isPostCardContext
            ? "vertical"
            : String(options?.metaDirection ?? previewEl?.dataset?.metaDirection ?? "");

        previewEl.innerHTML = ProductLinkerRender.buildCardHtml(product, {
            ...options,
            metaDirection: resolvedMetaDirection,
        });

        const thumb = productLinkerForm.getThumb(previewEl);
        const img = thumb instanceof HTMLElement ? productLinkerForm.getThumbImage(thumb) : null;
        if (thumb instanceof HTMLElement && img instanceof HTMLImageElement) {
            ProductLinkerListener.bindThumbImageError(img, () => {
                try {
                    if (!productLinkerForm.getThumbPlaceholder(thumb)) {
                        img.insertAdjacentHTML(
                            "beforebegin",
                            ProductLinkerRender.buildPlaceholderThumbHtml(
                                product?.category_id ?? product?.category?.category_id,
                            ),
                        );
                    }
                    img.remove();
                } catch (_error) {
                    void _error;
                }
            });
        }

        translator.updateUITranslations();
    }

    public static buildPlaceholderThumbHtml(categoryId: number | string | null | undefined = null): string {
        const symbol = resolveCategorySymbol(categoryId, (s) => productLinkerForm.hasCategoryIconSymbol(s));
        return `
            <div class="board-placeholder post-product-linker__thumb-placeholder" data-role="${Roles.POST_PRODUCT_THUMB_PLACEHOLDER}" data-placeholder-variant="no-image">
                ${buildCategoryPlaceholderHtml(symbol)}
            </div>
        `
            .replace(/>\s+</g, "><")
            .trim();
    }

    /**
     * Builds the "linked product" card markup (thumb, name, brand/category
     * meta, rating stats) as a plain HTML string — shared by the single
     * "Target Product" preview (renderProduct, below) and the WYSIWYG
     * product-embed toolbar feature, so both surfaces render the identical
     * common card rather than each having their own look.
     */
    public static buildCardHtml(
        product: ProductLinkerRenderableProduct,
        options: { targetBlank?: boolean; metaDirection?: string } = {},
    ): string {
        const productId = String(product?.product_id ?? "");
        const productName = String(product?.product_name ?? "");
        const resolvedBrandName = String(product?.brand_name ?? product?.brand?.brand_name ?? "");
        const resolvedDescription = String(product?.general_description ?? "").trim();
        const rawProductImage = String(product?.image_url ?? product?.image ?? product?.image_path ?? "");
        const resolvedBrandId = String(product?.brand_id ?? product?.brand?.brand_id ?? "").trim();

        const imageUrl = (() => {
            const normalizedImagePath = rawProductImage ? String(rawProductImage).trim().replace(/\\/g, "/") : "";
            if (!normalizedImagePath) return "";

            if (normalizedImagePath.toUpperCase() === "NO_IMAGE") return "";
            if (isDefaultNoImagePath(normalizedImagePath)) return "";
            if (normalizedImagePath.startsWith("http")) return normalizedImagePath;

            if (normalizedImagePath.startsWith("/") || normalizedImagePath.includes("/")) {
                return CommonUtils.resolveUrl(
                    normalizedImagePath.startsWith("/") ? normalizedImagePath : `/${normalizedImagePath}`,
                );
            }

            const productImageUrl = resolvedBrandId
                ? phpUrl.productImage(resolvedBrandId, normalizedImagePath)
                : phpUrl.productImage(normalizedImagePath);
            if (!productImageUrl || isDefaultNoImagePath(productImageUrl)) return "";
            return productImageUrl.startsWith("http") ? productImageUrl : CommonUtils.resolveUrl(productImageUrl);
        })();

        const productDetailUrl = CommonUtils.resolveUrl(`/products/${encodeURIComponent(productId)}`);
        const brandDetailUrl = resolvedBrandId
            ? CommonUtils.resolveUrl(`/brands/${encodeURIComponent(resolvedBrandId)}`)
            : "";

        const categoryLineHtml = buildCategoryBreadcrumbHtml({
            categoryId: product?.category_id ?? product?.category?.category_id,
            categoryName: product?.category_name ?? product?.category?.category_name,
            parentCategoryId: product?.parent_category_id ?? product?.category?.parent_category_id,
            parentCategoryName: product?.parent_category_name ?? product?.category?.parent_category_name,
        });
        const targetBlank = options?.targetBlank !== false;
        const resolvedCategoryId = product?.category_id ?? product?.category?.category_id;
        const categorySymbol = resolveCategorySymbol(resolvedCategoryId, (s) =>
            productLinkerForm.hasCategoryIconSymbol(s),
        );

        const placeholderHtml = ProductLinkerRender.buildPlaceholderThumbHtml(resolvedCategoryId);

        // The template below is indented for readability, but that whitespace becomes
        // significant (visible extra line boxes) once inserted into a `white-space:
        // pre-wrap` context like the Quill editor's .ql-editor — e.g. the indentation
        // around .post-product-linker__name-link's nested <div> inflated the name row
        // to ~78px tall. Collapsing purely-whitespace gaps between tags below keeps the
        // output visually identical everywhere regardless of the surrounding white-space.
        return `
            <div class="post-product-linker__card" data-product-id="${productId}">
                <div class="post-product-linker__card-link">
                    <div class="post-product-linker__thumb" data-role="${Roles.POST_PRODUCT_THUMB}"${resolvedCategoryId !== null && resolvedCategoryId !== undefined ? ` data-category-id="${resolvedCategoryId}"` : ""}>
                        <a class="post-product-linker__thumb-link" data-role="${Roles.POST_PRODUCT_THUMB_LINK}" href="${productDetailUrl}"${targetBlank ? ' target="_blank" rel="noopener noreferrer"' : ""}>
                            ${imageUrl ? `<img src="${imageUrl}" alt="" loading="lazy" referrerpolicy="no-referrer" />` : ""}
                            ${imageUrl ? "" : placeholderHtml}
                        </a>
                    </div>
                    <div class="post-product-linker__meta">
                        <div class="post-product-linker__name-row">
                            <a class="post-product-linker__name-link" href="${productDetailUrl}" title="${CommonUtils.escapeHtmlAttribute(productName)}"${targetBlank ? ' target="_blank" rel="noopener noreferrer"' : ""}>
                                <div class="post-product-linker__name">${productName}</div>
                            </a>
                        </div>
                        <div class="post-product-linker__brand-line">
                            ${buildBrandIconHtml(24)}
                            ${resolvedBrandName ? (brandDetailUrl ? `<a class="post-product-linker__brand-link" href="${brandDetailUrl}" title="${CommonUtils.escapeHtmlAttribute(resolvedBrandName)}">${resolvedBrandName}</a>` : `<span class="post-product-linker__brand-text" title="${CommonUtils.escapeHtmlAttribute(resolvedBrandName)}">${resolvedBrandName}</span>`) : '<span class="post-product-linker__brand-text text-muted">-</span>'}
                        </div>
                        <div class="post-product-linker__category-line">${buildCategoryIconHtml(categorySymbol)}${categoryLineHtml}</div>
                        ${renderProductRatingStatsHtml({
                            averageRating: product?.average_rating,
                            reviewCount: product?.review_count,
                            averageRole: Roles.PRODUCT_RATING_AVERAGE,
                            reviewCountRole: Roles.PRODUCT_REVIEW_COUNT,
                            includeDataRoles: true,
                        })}
                        ${resolvedDescription ? `<div class="post-product-linker__description" title="${CommonUtils.escapeHtmlAttribute(resolvedDescription)}">${CommonUtils.escapeHtml(resolvedDescription)}</div>` : ""}
                    </div>
                </div>
            </div>
        `
            .replace(/>\s+</g, "><")
            .trim();
    }
}

export { ProductLinkerRender };
